import { Component, OnInit } from '@angular/core';
import { AuthUser } from '../../../../globalTypes/auth.types';
import { ButtonComponent } from "../../component/layout/element/button.component";
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ListingAnswerPublic, ListingPublic, ListingQuestionPublic } from '../../../../globalTypes/listings.types';
import { lastValueFrom, take, takeUntil } from 'rxjs';
import { listingsFeature, ListingsPageActions } from '../../store/listings.store';
import { MatSelectModule, MatSelectChange } from '@angular/material/select';
import { CategorySingleSelectorComponent } from '../../component/layout/element/category-single-selector';
import { CategorySelected } from '../../component/layout/element/category-selector';
import { FormsModule } from '@angular/forms';
import { ListingService } from '../../services/listing.service';
import { accountFeature } from '../../store/account.store';
import { getAllCategories, getListingQuestionsForCategoryPublic, GetListingQuestionsForCategoryPublicData } from '@dataconnect/account';
import { Logger } from '../../helper/logger';
import { sortByKey } from '../../helper/general';
import { PoseQuestionComponent } from '../../component/layout/element/pose-question.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { CategoryPublic } from '../../../../globalTypes/category.types';
import { categoriesFeature, CategoriesPageActions } from '../../store/category.store';
import { LimiterDirective } from '../../directive/limiter';
const log = new Logger('DetailsComponent');

@Component({
  selector: 'msh-details',
  imports: [PoseQuestionComponent, FormsModule, CategorySingleSelectorComponent, MatSelectModule, ButtonComponent, LimiterDirective],
  template: `
      <div class="border-copy/35 border p-1 rounded w-full mb-7">
        <msh-category-single-selector
          (onSelectCategory)="selectCategory($event)"
        ></msh-category-single-selector>
      </div>
      @if(listing){
        <div>
          <input
            type="text"
            placeholder="Listing title"
            [(ngModel)]="listing.name"
            [limiter]="150"
            name="listing.name"
            required
            />
        </div>
        <div>
          <textarea
            placeholder="Listing Description"
            [(ngModel)]="listing.description"
            name="listing.description"
            [limiter]="1000"
            required
          ></textarea>
        </div>
      }@else{
        Something went wrong and the listing you were creating is no longer attached. Please go back a step.
      }
      @if(questions){
        @for(question of questions.listingQuestions; track question; let questionIndex = $index){
          <msh-pose-question
            [questionType]="question.questionType"
            [displayLabel]="question.displayLabel || ''"
            [isMandatory]="question.isMandatory"
            [options]="question.listingQuestionOptions_on_listingQuestion"
            [answer]="answers[question.id] || ''"
            (onChange)="answers[question.id] = $event"
          ></msh-pose-question>
        }
      }
      <div class="grow"></div>
      <msh-button (onClick)="onSubmitClick($event)" class="mb-14" [disabled]="!listing || !listing.name || !listing.description || !listing.categoryId" [isLoading]="isLoading" [cta]="cta"></msh-button>
  `,
  styles: `:host { display: flex; flex-direction: column; flex-grow: 1; height: 100%;}`
})
export class DetailsComponent extends Base<DetailsComponent> implements OnInit {
  listing: ListingPublic | Partial<ListingPublic> | null = null;
  authUser!: AuthUser;
  listingQuestionsLoadedForCategory?:number
  cta = 'Update';
  isLoading = false;
  questions?: GetListingQuestionsForCategoryPublicData;
  answers: {[questionId: string]: string | string[]} = {};

  // TODO: Add category specific questions

  constructor(
    private router: Router,
    private store: Store,
    private snackbar: MatSnackBar,
    private listingService: ListingService,
  ){
    super();
    this.store.select(listingsFeature.selectListing).pipe(takeUntil(this.$destroy)).subscribe(listing=>{
      if(!listing) return; 
      this.listing = JSON.parse(JSON.stringify(listing));
      if(!this.listing || !this.listing?.id || this.listing.id === 'new') {
        this.cta = 'Next';
      }
      this.loadListingQuestions();
    })
  }
  async ngOnInit(): Promise<void> {

    const dataRef = await getAllCategories();
    const categories = dataRef.data.categories as unknown as CategoryPublic[];
    this.store.dispatch(CategoriesPageActions.categoriesLoaded({categories}))
  }

  selectCategory(ev: CategorySelected){
    log.log({ev: ev, listing: this.listing});
    if(this.listing){
      this.listing = JSON.parse(JSON.stringify({...this.listing, categoryId: ev.id, category: {isDigital: ev.isDigital}}));
      this.loadListingQuestions();
    }
  }

  async loadListingQuestions(){
    log.log({category: this.listing?.categoryId})
    if(!this.listing || !this.listing.categoryId) return;
    const segment = await lastValueFrom(this.store.select(accountFeature.selectSegment).pipe(take(1)));
    const questionsRef = await getListingQuestionsForCategoryPublic({segmentId: segment?.id || 0, categoryId: this.listing.categoryId})
    const questions = questionsRef.data;
    log.log({questions, ans: this.listing.listingAnswers_on_listing})
    questions.listingQuestions.sort(sortByKey("sortPriority"))
    this.questions = questions;
    this.answers = this.listing.listingAnswers_on_listing?.reduce((acc, la) => {
      const key = la?.question?.id || "";
      const value = la?.question?.questionType === "TEXT" ? (la?.answer?.answerText || "") : (la?.answer?.answerValues || []);
      acc[key] = value;
      return acc;
    }, {} as {[questionId: string]: string | string[]}) || {}
    log.log({answers: this.answers, listingAnswers_on_listing:this.listing.listingAnswers_on_listing});
    this.listingQuestionsLoadedForCategory = this.listing.categoryId;
  }

  async onSubmitClick(ev: Event){
    log.log({ev});
    this.isLoading = true; 
    console.log({answers: this.answers});
    if(!this.listing) throw new Error('Listing not found');
    if(this.questions && this.questions.listingQuestions.length > 0){
      if(!this.listing.listingAnswers_on_listing) this.listing.listingAnswers_on_listing = [];
      this.listing.listingAnswers_on_listing = this.questions.listingQuestions.map(question => {
        const answer = question?.questionType === "TEXT" ? {answerText: this.answers[question.id]} : {answerValues: this.answers[question.id] }
         return {
          question: question as any,
          answer: answer as any
        } as ListingAnswerPublic
      })
    }
    this.listing.searchContents = this.listingService.compileSearchContents(this.listing)
    if(this.listing?.id === "new"){
      this.store.dispatch(ListingsPageActions.progressListing({listing: this.listing as Partial<ListingPublic>}))
      this.router.navigate(['manage-listing','price-stock', this.listing.id])
    } else {
      // [ ]- TEST THIS handle this.answers
      this.isLoading = true;
      await this.listingService.saveListing(this.listing);
      this.snackbar.open("Listing details have been saved", "close", {duration: 8000})
      this.isLoading = false;
    }
  }
}
